import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { Lock, Mail, AlertCircle, LogIn, Shield, User as UserIcon } from 'lucide-react';
import { GoogleAdminLogin } from '../../components/auth/GoogleAdminLogin';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'client' | 'admin'>('client');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await api.post('/auth/login', data);
      if (res.data?.success) {
        const { token, user, clientProfile } = res.data.data;
        login(token, user, clientProfile);

        // Redirect based on role
        if (user.role === 'SUPER_ADMIN' || user.role === 'ADMIN' || user.role === 'EDITOR') {
          navigate('/admin');
        } else {
          navigate('/client/dashboard');
        }
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error?.message || 'Invalid email or password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fillCredentials = (email: string) => {
    setValue('email', email);
    setValue('password', 'Password123!');
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-7 p-8 sm:p-10 rounded-3xl bg-white border border-editorial-border shadow-md">
        
        {/* Header */}
        <div className="text-center space-y-2.5">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-brand-500/50 bg-white p-0.5 mx-auto shadow-md flex items-center justify-center">
            <img
              src="/ftt-logo.png"
              alt="For The Truth Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </div>
          <h2 className="font-extrabold text-2xl sm:text-3xl text-slate-900 tracking-tight">
            Publishing Portal Login
          </h2>
          <p className="text-xs text-slate-500 font-sans">
            Access your manuscripts, contracts, proofs, and editorial communications.
          </p>
        </div>

        {/* Role Toggle Tabs */}
        <div className="flex rounded-xl bg-slate-100 p-1 border border-slate-200">
          <button
            type="button"
            onClick={() => {
              setActiveTab('client');
              setErrorMessage(null);
            }}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'client'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <UserIcon className="w-3.5 h-3.5 text-brand-600" />
            <span>Author / Client</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('admin');
              setErrorMessage(null);
            }}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'admin'
                ? 'bg-brand-900 text-white shadow-xs'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Shield className="w-3.5 h-3.5 text-amber-400" />
            <span>Staff &amp; Admin (Google)</span>
          </button>
        </div>

        {errorMessage && (
          <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {activeTab === 'client' ? (
          /* Client/Author Form */
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  {...register('email')}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="author@example.com"
                />
              </div>
              {errors.email && <p className="text-rose-600 text-[11px] mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Password
                </label>
                <Link to="/contact" className="text-[11px] text-brand-700 hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  {...register('password')}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="••••••••"
                />
              </div>
              {errors.password && <p className="text-rose-600 text-[11px] mt-1">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <span>Sign In as Author</span>
                  <LogIn className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </form>
        ) : (
          /* Admin Google Auth Tab */
          <div className="space-y-4 py-2">
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-950 space-y-1">
              <span className="font-bold flex items-center gap-1.5 text-amber-900">
                <Shield className="w-3.5 h-3.5 text-amber-600" />
                Google One-Click Authentication
              </span>
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Sign in with your approved Google administrative account to access editorial workflows and governance controls.
              </p>
            </div>

            <GoogleAdminLogin autoPrompt={true} />

            <div className="text-center pt-2">
              <Link
                to="/admin/login"
                className="text-[11px] font-semibold text-brand-700 hover:underline"
              >
                Open Full Dedicated Admin Portal &rarr;
              </Link>
            </div>
          </div>
        )}

        {/* Demo Fast-Fill helper */}
        <div className="pt-4 border-t border-slate-100 space-y-2">
          <span className="text-[10px] font-mono uppercase font-bold text-slate-400 block text-center">
            Demo Test Client
          </span>
          <button
            type="button"
            onClick={() => {
              setActiveTab('client');
              fillCredentials('author@solagratia.org');
            }}
            className="w-full p-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 text-left font-mono"
          >
            <div className="flex justify-between items-center text-[11px]">
              <strong>Author: Dr. Arthur Pendleton</strong>
              <span className="text-[10px] text-brand-600 font-bold">1-Click Fill</span>
            </div>
            <span className="block text-[10px] text-slate-500 mt-0.5">author@solagratia.org</span>
          </button>
        </div>

        <div className="text-center text-xs text-slate-500">
          <span>New author or publishing client? </span>
          <Link to="/register" className="font-bold text-brand-700 hover:underline">
            Create an Account
          </Link>
        </div>

      </div>
    </div>
  );
};

